import { z } from 'zod';

/**
 * Canonical GUIDE-S domain ids (Qualcon domains 1–7) stored on a call recording.
 */
export const GUIDES_DOMAIN_IDS = [
  'whatsOnYourMind',
  'safety',
  'practicalNeeds',
  'supportAtHome',
  'existingCareAndBarriers',
  'nextStepsDiscussed',
  'nextStepsUnderstood',
] as const;

/** GUIDE-S domain identifier. */
export type GuidesDomainId = (typeof GUIDES_DOMAIN_IDS)[number];

const GUIDES_CORE_DOMAIN_IDS: readonly GuidesDomainId[] = [
  'whatsOnYourMind',
  'safety',
  'practicalNeeds',
  'supportAtHome',
  'existingCareAndBarriers',
];

const GUIDES_NEXT_STEP_DOMAIN_IDS: readonly GuidesDomainId[] = [
  'nextStepsDiscussed',
  'nextStepsUnderstood',
];

/** Zod enum of GUIDE-S domain ids. */
export const GuidesDomainIdSchema = z.enum(GUIDES_DOMAIN_IDS);

/*
 * Call-clock label as it appears in a transcript line, e.g. `[Guide] (00:12):`.
 * Transcripts recorded before timestamps were added have no such label, so a
 * domain can be met with no clock to point at.
 */
const GUIDES_DOMAIN_TIMESTAMP_PATTERN = /^\d{1,2}:\d{2}(:\d{2})?$/;

/**
 * One GUIDE-S domain score plus model reasoning.
 * `timestamp` is the call-clock on the utterance that first made `met` true;
 * it is null when `met` is false and when the transcript carries no clock.
 */
export const GuidesDomainScoreSchema = z
  .object({
    id: GuidesDomainIdSchema,
    met: z.boolean(),
    reasoning: z.string(),
    timestamp: z.string().nullable().optional(),
  })
  .transform(score => {
    const trimmed =
      typeof score.timestamp === 'string' ? score.timestamp.trim() : '';
    const isClock = GUIDES_DOMAIN_TIMESTAMP_PATTERN.test(trimmed);
    return {
      ...score,
      timestamp: score.met && isClock ? trimmed : null,
    };
  });

/** Parsed LLM payload: the seven domain scores only. */
export const GuidesLlmDomainsResponseSchema = z
  .object({
    domains: z.array(GuidesDomainScoreSchema).length(GUIDES_DOMAIN_IDS.length),
  })
  .superRefine((value, ctx) => {
    const ids = new Set(value.domains.map(domain => domain.id));
    if (ids.size !== GUIDES_DOMAIN_IDS.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Response must include each GUIDE-S domain exactly once',
        path: ['domains'],
      });
    }
  });

/**
 * Persisted GUIDE-S analysis on a voice call recording.
 */
export const GuidesDomainAnalysisSchema = z.object({
  promptVersion: z.string().min(1),
  modelId: z.string().min(1),
  analyzedAt: z.string().min(1),
  hospitalizationId: z.number().int().nullable(),
  domains: z.array(GuidesDomainScoreSchema).length(GUIDES_DOMAIN_IDS.length),
  metCountDomains1to5: z.number().int(),
  metCountNextSteps: z.number().int(),
});

/** Optional reprocess body: Vertex model override. */
export const RerunGuidesDomainAnalysisRequestSchema = z.object({
  modelId: z.string().min(1).optional(),
});

/** One domain score. */
export type GuidesDomainScore = z.infer<typeof GuidesDomainScoreSchema>;

/** LLM JSON before metadata is attached. */
export type GuidesLlmDomainsResponse = z.infer<
  typeof GuidesLlmDomainsResponseSchema
>;

/** Stored analysis document. */
export type GuidesDomainAnalysisDto = z.infer<typeof GuidesDomainAnalysisSchema>;

/** POST /calls/:id/rerun-guides-analysis body. */
export type RerunGuidesDomainAnalysisRequestDto = z.infer<
  typeof RerunGuidesDomainAnalysisRequestSchema
>;

/**
 * Counts Qualcon-style rollups from domain scores (do not trust the model).
 * @param domains Scored GUIDE-S domains.
 * @returns Met counts for domains 1–5 and next-step domains 6–7.
 */
export function computeGuidesDomainCounts(domains: GuidesDomainScore[]): {
  metCountDomains1to5: number;
  metCountNextSteps: number;
} {
  const byId = new Map(domains.map(domain => [domain.id, domain]));
  const countMet = (ids: readonly GuidesDomainId[]): number =>
    ids.reduce((sum, id) => sum + (byId.get(id)?.met ? 1 : 0), 0);

  return {
    metCountDomains1to5: countMet(GUIDES_CORE_DOMAIN_IDS),
    metCountNextSteps: countMet(GUIDES_NEXT_STEP_DOMAIN_IDS),
  };
}

/**
 * Orders domain scores into the canonical GUIDE-S sequence.
 * @param domains Unordered domain scores.
 * @returns Domains in {@link GUIDES_DOMAIN_IDS} order.
 */
export function orderGuidesDomains(
  domains: GuidesDomainScore[]
): GuidesDomainScore[] {
  const byId = new Map(domains.map(domain => [domain.id, domain]));
  return GUIDES_DOMAIN_IDS.map(id => {
    const domain = byId.get(id);
    if (!domain) {
      throw new Error(`Missing GUIDE-S domain ${id}`);
    }
    return domain;
  });
}

/** Inputs used to persist a GUIDE-S analysis document. */
export type BuildGuidesDomainAnalysisParams = {
  promptVersion: string;
  modelId: string;
  analyzedAt: string;
  hospitalizationId: number | null;
  domains: GuidesDomainScore[];
};

/**
 * Builds the persisted analysis document with derived counts.
 * @param params Prompt metadata and LLM domain scores.
 * @returns Validated {@link GuidesDomainAnalysisDto}.
 */
export function buildGuidesDomainAnalysis(
  params: BuildGuidesDomainAnalysisParams
): GuidesDomainAnalysisDto {
  const domains = orderGuidesDomains(params.domains);
  const counts = computeGuidesDomainCounts(domains);
  return GuidesDomainAnalysisSchema.parse({
    promptVersion: params.promptVersion,
    modelId: params.modelId,
    analyzedAt: params.analyzedAt,
    hospitalizationId: params.hospitalizationId,
    domains,
    ...counts,
  });
}
